import { FC, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useStyles from "./styles";
import {
  PlanFeatureState,
  useGetFeatureCatalogue,
  useUpdatePlanFeatures,
} from "../../api/billingFeatures";
import { useAuth } from "../../auth/useAuth";

/**
 * Plan feature editor.
 *
 * What each tier includes ("Everything in Essential", "Geo-location", "Tax
 * optimization"...) used to exist in three disconnected places: the seeder, the
 * fleet app's marketing copy, and the fleet app's FEATURE_PLAN_MAP. Changing an
 * offer meant a backend deploy AND a frontend deploy, and the three drifted.
 *
 * Now it is one list on `plans.features`, edited here, enforced by the
 * EnforceQuota middleware and read by the fleet app at runtime.
 *
 * INHERITANCE
 * -----------
 * "Everything in Essential" is stored as the marker `everything_in_essential`,
 * not as a flattened copy. Inherited features are therefore shown ticked but
 * DISABLED: unticking one here would be a lie, since the marker would put it
 * straight back. To remove an inherited feature the operator must either drop
 * the inheritance or edit the parent plan - which is the honest model.
 */
export const BillingFeaturesTab: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { can } = useAuth();

  const canEdit = can("billing.plan.update");

  const { catalogue, isLoading } = useGetFeatureCatalogue();
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [draft, setDraft] = useState<string[]>([]);

  const plan: PlanFeatureState | null = useMemo(
    () => catalogue.plans.find((p) => p.id === selectedPlanId) ?? null,
    [catalogue.plans, selectedPlanId]
  );

  // Select the first plan once data lands, and reset the draft whenever the
  // selection changes so a half-finished edit never leaks onto another plan.
  useEffect(() => {
    if (!catalogue.plans.length) return;
    if (selectedPlanId === null) {
      setSelectedPlanId(catalogue.plans[0].id);
    }
  }, [catalogue.plans, selectedPlanId]);

  useEffect(() => {
    setDraft(plan ? [...plan.features] : []);
  }, [plan]);

  const save = useUpdatePlanFeatures();

  /**
   * Features the plan gets through an `everything_in_*` marker rather than
   * directly. Computed by expanding the markers currently in the draft against
   * the other plans' effective lists, so the preview reacts as the operator
   * ticks inheritance on and off.
   */
  const inheritedKeys = useMemo(() => {
    const out = new Set<string>();
    draft
      .filter((key) => key.startsWith("everything_in_"))
      .forEach((marker) => {
        const parentSlug = marker.replace("everything_in_", "");
        const parent = catalogue.plans.find((p) => p.slug === parentSlug);
        parent?.effective_features.forEach((f) => out.add(f));
      });
    return out;
  }, [draft, catalogue.plans]);

  const toggle = (key: string) => {
    if (!canEdit) return;
    setDraft((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const dirty =
    !!plan &&
    JSON.stringify([...draft].sort()) !==
      JSON.stringify([...plan.features].sort());

  if (isLoading) {
    return (
      <div className={classes.emptyState}>
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <>
      <div className={classes.infoBox}>
        {t(
          "billing.featuresInfo",
          "Les fonctionnalites cochees ici determinent ce a quoi les clients de l'offre ont acces dans l'application. La modification prend effet immediatement, y compris pour les abonnements en cours."
        )}
      </div>

      {!canEdit && (
        <div className={classes.warningBox} role="status">
          <span>
            {t(
              "billing.featuresReadOnly",
              "Lecture seule : la modification des offres requiert la permission billing.plan.update."
            )}
          </span>
        </div>
      )}

      {/* Plan picker */}
      <div className={classes.planTabs}>
        {catalogue.plans.map((p) => (
          <Chip
            key={p.id}
            label={p.name}
            onClick={() => setSelectedPlanId(p.id)}
            color={p.id === selectedPlanId ? "primary" : "default"}
            variant={p.id === selectedPlanId ? "filled" : "outlined"}
            size="small"
          />
        ))}
      </div>

      {plan && (
        <>
          {/* Inheritance: "Everything in X" */}
          <div className={classes.featureGroup}>
            <h4 className={classes.featureGroupTitle}>
              {t("billing.inheritance", "Herite d'une autre offre")}
            </h4>
            <p className={classes.cardHint}>
              {t(
                "billing.inheritanceHint",
                "Cocher \u00ab Tout Essential \u00bb reprend automatiquement les fonctionnalites d'Essential, y compris celles qui y seront ajoutees plus tard."
              )}
            </p>
            <div className={classes.featureList}>
              {catalogue.inheritance
                // A plan cannot inherit from itself; the API rejects it, so do
                // not offer it.
                .filter((option) => option.slug !== plan.slug)
                .map((option) => (
                  <FormControlLabel
                    key={option.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={draft.includes(option.key)}
                        onChange={() => toggle(option.key)}
                        disabled={!canEdit}
                      />
                    }
                    label={option.label}
                  />
                ))}
            </div>
          </div>

          {/* Concrete features, grouped */}
          {catalogue.groups.map((group) => (
            <div key={group.group} className={classes.featureGroup}>
              <h4 className={classes.featureGroupTitle}>{group.label}</h4>
              <div className={classes.featureList}>
                {group.features.map((feature) => {
                  const direct = draft.includes(feature.key);
                  const inherited = inheritedKeys.has(feature.key);

                  return (
                    <Tooltip
                      key={feature.key}
                      title={
                        inherited && !direct
                          ? t(
                              "billing.featureInherited",
                              "Incluse via une offre heritee. Retirez l'heritage ou modifiez l'offre parente."
                            )
                          : feature.description ?? ""
                      }
                      placement="top"
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={direct || inherited}
                            // Inherited entries are shown ticked but locked:
                            // unticking would be reverted by the marker.
                            disabled={!canEdit || (inherited && !direct)}
                            onChange={() => toggle(feature.key)}
                          />
                        }
                        label={
                          <span className={classes.featureLabel}>
                            {feature.label}
                            {inherited && !direct && (
                              <span className={classes.inheritedBadge}>
                                {t("billing.inherited", "Herite")}
                              </span>
                            )}
                          </span>
                        }
                      />
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          ))}

          <div className={classes.featureActions}>
            <span className={classes.cardHint}>
              {t("billing.effectiveCount", "{{count}} fonctionnalites au total", {
                count: new Set([
                  ...draft.filter((k) => !k.startsWith("everything_in_")),
                  ...Array.from(inheritedKeys),
                ]).size,
              })}
            </span>
            <div>
              <Button
                onClick={() => setDraft(plan ? [...plan.features] : [])}
                disabled={!dirty || save.isPending}
              >
                {t("common.reset", "Annuler")}
              </Button>
              <Button
                variant="contained"
                disabled={!canEdit || !dirty || save.isPending}
                onClick={() =>
                  save.mutate({ planId: plan.id, features: draft })
                }
                startIcon={
                  save.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                {t("common.save", "Enregistrer")}
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BillingFeaturesTab;
